import { useEffect, useRef, useState } from 'react';
import { Button, Card, CardTitle, EmptyState, useNow } from '@wundo/ui';
import { ClipboardList, Sparkles } from 'lucide-react';
import type {
  MeetingPageResponse,
  MeetingReportSection,
} from '@wundo/shared/meetingPage.ts';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { MeetingActionItems } from './MeetingActionItems.tsx';
import { MeetingMaterialList } from './MeetingMaterialList.tsx';
import { MeetingReportSectionCard } from './MeetingReportSectionCard.tsx';
import { MeetingCopyButton } from './MeetingCopyButton.tsx';
import { MeetingShareNotesDialog } from './MeetingShareNotesDialog.tsx';
import { meetingReportState } from './meetingReportState.ts';
import type { RichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { m } from '@/paraglide/messages.js';

/* Long enough that a sentence is one save, short enough to feel instant. */
const SAVE_AFTER_MS = 600;

/**
 * What a verslag that is on its way is coming from: the user's own recording,
 * the notetaker's, or a writer already running.
 */
export type MeetingReportWaiting = 'microphone' | 'notetaker' | 'writing';

export interface MeetingReportPanelProps {
  page: MeetingPageResponse;
  waiting: MeetingReportWaiting | null;
  /* The user's own recording could not be read back. */
  recordingFailed: boolean;
  generating: boolean;
  generateFailed: boolean;
  copied: boolean;
  editor: RichTextEditorWiring;
  onGenerate: () => void;
  /** One gesture for the shared notes: keep them, and write from them. */
  onPasteGenerate: (content: string) => void;
  /** The note's full text, for the dialog to open with. */
  onLoadNote: () => Promise<string | null>;
  onRegenerate: () => void;
  onSave: (sections: MeetingReportSection[]) => void;
  onDeleteMaterial: (materialId: string) => void;
  onCopy: () => void;
  onToggleActionItem: (itemId: string, done: boolean) => void;
  onEditActionItem: (itemId: string, text: string) => void;
  onAddActionItem: (text: string) => void;
  onDeleteActionItem: (itemId: string) => void;
}

/**
 * The Verslag tab: what happened, and what was agreed.
 *
 * The tab shows one thing at a time, and which one depends on the situation it
 * is in rather than on what the page can do. Where the notetaker joins, the
 * verslag writes itself and the tab says so and offers nothing; where Wundo
 * could not be in the room, one button invites the user to share their own
 * notes, and a dialog with one big field is the whole flow; where a verslag
 * exists, the document is the page and the material it was written from
 * shrinks to one quiet line under it, with nothing to press beside it.
 *
 * Nothing here is generated by opening the page: writing a verslag is always an
 * explicit ask, or the transcript's arrival on the server.
 *
 * The document is held in local state while it is typed into and written back
 * on a short debounce, so a keystroke never waits on the network and the caret
 * never jumps. The agreements underneath are the thread's own live list: ticking
 * one off here, on the preparation, or by telling Wundo in chat is the same act.
 */
export function MeetingReportPanel({
  page,
  waiting,
  recordingFailed,
  generating,
  generateFailed,
  copied,
  editor,
  onGenerate,
  onPasteGenerate,
  onLoadNote,
  onRegenerate,
  onSave,
  onDeleteMaterial,
  onCopy,
  onToggleActionItem,
  onEditActionItem,
  onAddActionItem,
  onDeleteActionItem,
}: MeetingReportPanelProps) {
  const [sections, setSections] = useState<MeetingReportSection[] | null>(
    page.report?.sections ?? null,
  );
  /*
   * Whether the user has changes the server has not acknowledged. While that is
   * true a refetch must not overwrite what they are typing — the page is the
   * authority on its own document until the save lands.
   */
  const unsaved = useRef(false);
  const now = useNow();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /*
   * The share-notes dialog, and what it opens with. Null while closed; the
   * note's current text (or '' when there is none) while open — fetched on
   * opening, because the page itself only carries an excerpt.
   */
  const [sharing, setSharing] = useState<string | null>(null);

  const fromServer = page.report?.sections ?? null;
  const stamp = fromServer ? JSON.stringify(fromServer) : null;

  useEffect(() => {
    if (unsaved.current) return;
    setSections(stamp ? (JSON.parse(stamp) as MeetingReportSection[]) : null);
  }, [stamp]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const write = (next: MeetingReportSection[]) => {
    setSections(next);
    unsaved.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      unsaved.current = false;
      onSave(next);
    }, SAVE_AFTER_MS);
  };

  const changeSection = (title: string, text: string) => {
    if (!sections) return;
    write(
      sections.map((section) =>
        section.title === title ? { ...section, text } : section,
      ),
    );
  };

  /*
   * The agreements that came out of this conversation, not the whole thread's:
   * the preparation is where the open list of everything lives, and repeating
   * it here would make the same line look like two.
   */
  const own = page.action_items.filter((item) => item.meeting_id === page.meeting.id);

  const state = meetingReportState(page, now);
  const transcribed = page.materials.some((material) => material.kind === 'transcript');
  const pasted = page.materials.some((material) => material.kind === 'pasted');

  /* A recording that could not be read back would otherwise go on promising one. */
  const invitation = recordingFailed
    ? m.meeting_offline_failed_body()
    : state === 'paste'
      ? m.meeting_report_invite_body()
      : m.meeting_report_auto();
  const busy = waiting !== null;

  const inviteTitle =
    waiting === 'microphone' || waiting === 'notetaker'
      ? m.meeting_report_pending_title()
      : recordingFailed
        ? m.meeting_offline_failed_title()
        : m.meeting_report_invite_title();

  const description =
    waiting === 'microphone' ? (
      <>
        {m.meeting_offline_processing_body()}
        <span className='mt-2 block text-sm'>{m.meeting_offline_processing_note()}</span>
      </>
    ) : waiting === 'notetaker' ? (
      m.meeting_report_notetaker_body()
    ) : waiting === 'writing' ? (
      m.meeting_report_generating()
    ) : (
      invitation
    );

  /*
   * Opening the dialog means loading what it opens with: the note as it
   * stands, or nothing when there is none yet. A load that fails opens
   * nothing — an empty field over an existing note would invite saving
   * blankness over it.
   */
  const openShare = () => {
    void onLoadNote()
      .then((content) => setSharing(content ?? ''))
      .catch(() => setSharing(null));
  };

  /*
   * The way in, matched to the promise: the tab's whole invitation where nothing
   * is coming on its own, the quiet way past the notetaker's wait, and nothing
   * while the material is already in and being written up.
   */
  const shareInvite =
    waiting === 'notetaker' ? (
      <Button size='sm' variant='quiet' onClick={openShare}>
        {m.meeting_report_paste_now()}
      </Button>
    ) : !busy && state === 'paste' ? (
      <Button size='sm' onClick={openShare}>
        <ClipboardList className='size-4' aria-hidden='true' />
        {m.meeting_report_share_notes()}
      </Button>
    ) : undefined;

  /* Only reachable before a verslag exists, so it always writes one. */
  const submitNotes = (content: string) => {
    setSharing(null);
    onPasteGenerate(content);
  };

  /* Which sources the verslag was written from, in the order they are read. */
  const sources = [
    transcribed ? m.meeting_report_sources_transcript() : null,
    pasted ? m.meeting_report_sources_notes() : null,
  ].filter((source) => source !== null);

  return (
    <div className='mt-6 flex flex-col gap-6'>
      {/* One card, as the prep is: the verslag is a document, not a stack of blocks. */}
      <Card as='section' padding='document'>
        <div className='flex items-center justify-between gap-3'>
          <CardTitle>{m.meeting_tab_report()}</CardTitle>
          {sections && <MeetingCopyButton copied={copied} onCopy={onCopy} />}
        </div>

        {!sections && (
          <>
            <EmptyState
              headingLevel='h3'
              title={inviteTitle}
              busy={busy}
              description={description}
              action={shareInvite}
            />

            {/*
              The material and the button that writes from it — the retry after
              a failed write. None of it while a verslag is on its way: Wundo
              is writing from that material already.
            */}
            {!busy && page.materials.length > 0 && (
              <div className='flex flex-col gap-3 border-t border-fill pt-4'>
                <MeetingMaterialList
                  materials={page.materials}
                  onDelete={onDeleteMaterial}
                />
                <div className='flex flex-col items-start gap-2'>
                  {generateFailed && (
                    <p role='alert' className='text-sm text-danger'>
                      {m.meeting_report_generate_failed()}
                    </p>
                  )}
                  {/* Nothing to write from is not a disabled button, it is no button. */}
                  {page.report_available && (
                    <Button size='sm' disabled={generating} onClick={onGenerate}>
                      <Sparkles className='size-4' aria-hidden='true' />
                      {generateFailed
                        ? m.meeting_report_generate_retry()
                        : m.meeting_report_generate()}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {sections && (
          <>
            {/*
              Where the text came from, said once and quietly. The transcript
              itself is never shown — only that it was read.
            */}
            {page.report && (
              <p className='mt-2 text-xs text-ink-muted italic'>
                {m.meeting_report_provenance({
                  date: formatDayAndMonth(new Date(page.report.generated_at)),
                })}
              </p>
            )}

            <div className='mt-4 flex flex-col gap-4'>
              {sections.map((section) => (
                <MeetingReportSectionCard
                  key={section.title}
                  section={section}
                  editor={editor}
                  onChange={(text) => changeSection(section.title, text)}
                />
              ))}
            </div>

            {/*
              What it was written from, in one line and nothing more. Once the
              verslag is here it is the document that is edited, by typing in
              it — the material behind it is history.
            */}
            <div className='mt-4 flex flex-wrap items-center gap-x-2 gap-y-1'>
              {sources.length > 0 && (
                <span className='text-xs text-ink-muted'>
                  {m.meeting_report_sources({ sources: sources.join(' · ') })}
                </span>
              )}
            </div>

            <div className='mt-5 border-t border-fill pt-5'>
              <MeetingActionItems
                items={own}
                title={m.meeting_report_actions_title()}
                emptyLabel={m.meeting_report_actions_empty()}
                threadId={page.thread?.id ?? null}
                doneCount={page.action_items_done_count}
                onToggle={onToggleActionItem}
                onEdit={onEditActionItem}
                onAdd={onAddActionItem}
                onDelete={onDeleteActionItem}
              />
            </div>

            {/* No rule across the card when there is nothing to put under it. */}
            {(page.report_available || generating || generateFailed) && (
              <div className='mt-5 flex flex-wrap items-center gap-3 border-t border-fill pt-4'>
                {page.report_available && (
                  <Button
                    size='sm'
                    variant='ghost'
                    loading={generating}
                    onClick={onRegenerate}
                  >
                    {!generating && <Sparkles className='size-4' aria-hidden='true' />}
                    {m.meeting_report_regenerate()}
                  </Button>
                )}
                {generating && (
                  <span className='text-sm text-ink-muted'>
                    {m.meeting_report_generating()}
                  </span>
                )}
                {generateFailed && (
                  <span role='alert' className='text-sm text-danger'>
                    {m.meeting_report_generate_failed()}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Mounted per opening, so it always starts from what was loaded. */}
      {sharing !== null && (
        <MeetingShareNotesDialog
          open
          pending={generating}
          initial={sharing === '' ? null : sharing}
          submitLabel={m.meeting_report_generate()}
          onSubmit={submitNotes}
          onCancel={() => setSharing(null)}
        />
      )}
    </div>
  );
}
